import { List, ListItemProperty } from "@/features/list";
import { trpc, RouterOutputs } from "@/hooks/trpc";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EquipmentStatusEnum, EquipmentId, type CalendarDate } from "@brewbase/db/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/inventory/equipment/")({
  component: RouteComponent,
});

type EquipmentListItem = RouterOutputs["equipment"]["list"][number];

// Map EquipmentListItem to a simpler type compatible with List's RecordType constraint
// This strips branded types (like EquipmentId) back to their base types
type SimpleEquipmentListItem = {
  id: number;
  equipment_model_id: number;
  name: string;
  serial_number: string | null;
  location_id: number | null;
  status: string;
  commissioned_at: CalendarDate | null;
  decommissioned_at: CalendarDate | null;
  notes: string | null;
  created_at: Date;
  updated_at: Date;
  created_by: number | null;
  last_updated_by: number | null;
  deleted_at: Date | null;
  deleted_by: number | null;
  modelName: string;
  typeName: string;
  locationName: string | null;
};

function RouteComponent() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [statusFilter, setStatusFilter] = useState<EquipmentStatusEnum | "ALL">(
    "ALL",
  );
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data: equipment, isLoading } = trpc.equipment.list.useQuery(
    statusFilter === "ALL"
      ? { includeOutOfService: true }
      : { status: statusFilter },
  );

  const { data: models } = trpc.equipment.models.list.useQuery();

  if (isLoading || !equipment) {
    return <div className="flex-1 px-4 py-6">Loading equipment...</div>;
  }

  return (
    <div className="flex-1 px-4 py-6 space-y-6 flex flex-col min-h-0">
      <List
        data={equipment.map((e) => ({
          id: e.id.toString(),
          href: `/inventory/equipment/${e.id}`,
          properties: e,
        }))}
        properties={listColumns}
        customToolbar={
          <div className="flex items-center gap-2">
            <Select
              value={statusFilter}
              onValueChange={(v) =>
                setStatusFilter(v as EquipmentStatusEnum | "ALL")
              }
            >
              <SelectTrigger className="w-36 h-8">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Status</SelectItem>
                {Object.values(EquipmentStatusEnum).map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCreateOpen(true)}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Equipment
            </Button>
          </div>
        }
      />

      <CreateEquipmentDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        models={models ?? []}
      />
    </div>
  );
}

const listColumns: ListItemProperty<
  SimpleEquipmentListItem,
  keyof SimpleEquipmentListItem
>[] = [
  {
    key: "name",
    label: "Name",
    fieldType: "string",
  },
  {
    key: "modelName",
    label: "Model",
    fieldType: "string",
  },
  {
    key: "typeName",
    label: "Type",
    fieldType: "string",
  },
  {
    key: "status",
    label: "Status",
    fieldType: "string",
    render: (value) => {
      const status = value as EquipmentStatusEnum;
      return (
        <Badge
          variant={
            status === EquipmentStatusEnum.ACTIVE
              ? "default"
              : status === EquipmentStatusEnum.INACTIVE
                ? "secondary"
                : "outline"
          }
        >
          {status}
        </Badge>
      );
    },
  },
  {
    key: "locationName",
    label: "Location",
    fieldType: "string",
  },
  {
    key: "serial_number",
    label: "Serial #",
    fieldType: "string",
  },
];

interface CreateEquipmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  models: RouterOutputs["equipment"]["models"]["list"];
}

function CreateEquipmentDialog({
  open,
  onOpenChange,
  models,
}: CreateEquipmentDialogProps) {
  const utils = trpc.useUtils();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [modelId, setModelId] = useState<string>("");
  const [serialNumber, setSerialNumber] = useState("");

  const createMutation = trpc.equipment.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Created "${data.name}"`);
      utils.equipment.list.invalidate();
      onOpenChange(false);
      setName("");
      setModelId("");
      setSerialNumber("");
      navigate({
        to: "/inventory/equipment/$equipment_id",
        params: { equipment_id: data.id as EquipmentId },
      });
    },
    onError: (err) => {
      toast.error(`Failed to create: ${err.message}`);
    },
  });

  const handleCreate = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!modelId) {
      toast.error("Model is required");
      return;
    }
    createMutation.mutate({
      name,
      equipmentModelId: parseInt(
        modelId,
      ) as RouterOutputs["equipment"]["models"]["list"][number]["id"],
      serialNumber: serialNumber || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Equipment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Equipment name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="model">Model</Label>
            <Select value={modelId} onValueChange={setModelId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a model..." />
              </SelectTrigger>
              <SelectContent>
                {models.map((model) => (
                  <SelectItem key={model.id} value={String(model.id)}>
                    {model.name} ({model.typeName})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="serial">Serial Number (optional)</Label>
            <Input
              id="serial"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="Serial number"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={createMutation.isPending}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
